import { inject, observer } from "mobx-react";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import ProductStore from "../ProductStore";
import {
  CustomPricingInputModel,
  PackagePricingInputModel,
  PriceTierInputData,
  ProductInventorySettingsInputModel,
  ProductPricingPageInputModel,
  ProductReOrderSettingsInputModel,
  StandardPricingInputModel,
} from "../../../shared/models";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  InputFieldModel,
  IOption,
  MultiSelect,
  nameof,
  SapphireBlueButton,
  SingleSelect,
  theme,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CarouselComponent,
  CustomPricing,
  PackagePricing,
  StandardPricing,
  TierPricingComponent,
} from "../../../shared/components";
import {
  ItemReOrderDurations,
  ItemReOrderIntervals,
  PricingModel,
} from "../../../shared/enums";
import {
  AdvertisingBlockWrapper,
  DescriptionCell,
  ItemContainer,
  LabelCell,
  MainContent,
  MembershipRightBar,
  OptionSelectorCard,
  ProductDetailWrapper,
} from "../../../shared/styles";
import { AccountSettingsStore } from "../../../shared/store";
import { StyledCarousel } from "../../../shared/components/PricingBuilder";
import { Carousel } from "react-bootstrap";

type Props = {
  productStore?: ProductStore;
  navigate: NavigateFunction;
};

@inject("productStore")
@observer
class ProductPricingPage extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  get hasVariants(): boolean {
    return this.props?.productStore?.detailPage?.hasVariants || false;
  }

  get pricingPage(): ProductPricingPageInputModel {
    return (
      this.props.productStore?.pricingPage || new ProductPricingPageInputModel()
    );
  }

  get volumePrice(): number {
    return parseFloat(this.pricingPage?.volumePricing?.price || "") || 0;
  }
  get tieredPrice(): number {
    return parseFloat(this.pricingPage?.tieredPricing?.price || "") || 0;
  }

  get getPricingInputs(): React.ReactNode {
    const pricingOption = this.pricingPage.pricingModel;
    switch (pricingOption) {
      case PricingModel.Standard:
        return (
          <StandardPricing
            standardPricing={
              this.pricingPage.standardPricing ||
              new StandardPricingInputModel()
            }
            onUpdate={(updatedStandardPricing) =>
              this.props.productStore?.updatePricingPage(
                new ProductPricingPageInputModel({
                  ...this.pricingPage,
                  standardPricing: updatedStandardPricing,
                })
              )
            }
          />
        );
      case PricingModel.Volume:
        return (
          <TierPricingComponent
            totalPrice={this.volumePrice}
            pricingType={PricingModel.Volume}
            tier={this.pricingPage.volumePricing || new PriceTierInputData()}
            onUpdateTier={(updatedValue) =>
              this.props.productStore?.updatePricingPage(
                new ProductPricingPageInputModel({
                  ...this.pricingPage,
                  volumePricing: updatedValue,
                })
              )
            }
          />
        );
      case PricingModel.Tiered:
        return (
          <TierPricingComponent
            totalPrice={this.tieredPrice}
            pricingType={PricingModel.Tiered}
            tier={this.pricingPage.tieredPricing || new PriceTierInputData()}
            onUpdateTier={(updatedValue) =>
              this.props.productStore?.updatePricingPage(
                new ProductPricingPageInputModel({
                  ...this.pricingPage,
                  tieredPricing: updatedValue,
                })
              )
            }
          />
        );
      case PricingModel.Package:
        return (
          <PackagePricing
            package={
              this.pricingPage.packagePricing || new PackagePricingInputModel()
            }
            onUpdate={(updatedData) =>
              this.props.productStore?.updatePricingPage(
                new ProductPricingPageInputModel({
                  ...this.pricingPage,
                  packagePricing: updatedData,
                })
              )
            }
          />
        );
      case PricingModel.Custom:
        return (
          <CustomPricing
            onUpdate={(updatedCustomPricing) =>
              this.props.productStore?.updatePricingPage(
                new ProductPricingPageInputModel({
                  ...this.pricingPage,
                  customPricing: updatedCustomPricing,
                })
              )
            }
            customPricing={
              this.pricingPage.customPricing || new CustomPricingInputModel()
            }
          />
        );
      default:
        return <></>;
    }
  }

  getSelectOptions(dataKey: string): IOption[] {
    switch (dataKey) {
      case nameof<ProductReOrderSettingsInputModel>("itemReOrderIntervals"):
        return Object.keys(ItemReOrderIntervals).map((key: string) => {
          return {
            value: key,
            label:
              ItemReOrderIntervals[key as keyof typeof ItemReOrderIntervals],
          };
        });
      case nameof<ProductPricingPageInputModel>("pricingModel"):
        return Object.keys(PricingModel).map((key: string) => {
          return {
            value: key,
            label: PricingModel[key as keyof typeof PricingModel],
          };
        });
      case nameof<ProductInventorySettingsInputModel>("isInStock"):
        return [
          { label: "In Stock", value: "InStock" },
          { label: "Out of Stock", value: "OutofStock" },
        ];
    }

    return [];
  }

  getSelectedValuesForMultiSelect(field: InputFieldModel): any[] {
    const options = this.getSelectOptions(field.dataKey);
    const keys = field.value?.toString().split(",") || [];
    return options.filter((f) => keys.indexOf(f.value) > -1);
  }

  singleSelect(
    field: string,
    selectedKey: string,
    hasError: boolean,
    className: string
  ): React.ReactNode {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={hasError}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[this.getSelectOptions(field) || []]}
        onChange={(option: any) =>
          this.onSingleSelectChange(field, option.value)
        }
        className={className}
      />
    );
  }

  multiSelect(
    field: string,
    selectedOptions: IOption[],
    className: string,
    classNamePrefix: string
  ): React.ReactNode {
    return (
      <MultiSelect
        options={this.getSelectOptions(field) || []}
        className={`${className} `}
        classNamePrefix={classNamePrefix}
        selectedOptions={selectedOptions || []}
        onChange={(newValue: IOption[]) =>
          this.onMultiSelectChange(field, newValue)
        }
      />
    );
  }

  checkBox(
    field: string,
    label: string,
    checked: boolean,
    disabled?: boolean
  ): React.ReactNode {
    return (
      <Input
        value={checked}
        id={field}
        type="checkbox"
        disabled={disabled}
        label={label}
        onChange={(e) => this.onInputChange(field, e.target.checked)}
      />
    );
  }

  textField(
    type: "text" | "number",
    field: string,
    placeHolder: string,
    value: string,
    hasError: boolean,
    className?: string
  ): React.ReactNode {
    return (
      <Input
        value={value}
        placeholder={placeHolder}
        id={field}
        type={type}
        error={hasError}
        onChange={(e) => this.onInputChange(field, e.target.value)}
        className={className}
      />
    );
  }

  onInputChange = (field: string, value: boolean | string) => {
    const clonedPricingPage = new ProductPricingPageInputModel(
      this.props.productStore?.pricingPage
    );
    if (!clonedPricingPage) return;
    switch (field) {
      case "isTrackInventory":
        clonedPricingPage.inventorySettings.isTrackInventory = value as boolean;
        break;
      case "isPromote":
        clonedPricingPage.inventorySettings.isPromote = value as boolean;
        break;
      case "isLimitPurchaseQuantity":
        clonedPricingPage.inventorySettings.isLimitPurchaseQuantity =
          value as boolean;
        break;
      case "isSellOnOutOfStock":
        clonedPricingPage.inventorySettings.isSellOnOutOfStock =
          value as boolean;
        break;
      case "allowReOrder":
        clonedPricingPage.reOrderSettings.allowReOrder = value as boolean;
        break;
      case "discountPercentage":
        clonedPricingPage.reOrderSettings.discountPercentage = value as string;
        break;
      case "quantityLimit":
        clonedPricingPage.inventorySettings.quantityLimit = value as string;
        break;
      case "available":
        clonedPricingPage.inventorySettings.available = value as string;
        break;
    }

    this.props.productStore?.updatePricingPage(clonedPricingPage);
  };

  onMultiSelectChange = (field: string, options: IOption[]) => {
    const clonedPricingPage = new ProductPricingPageInputModel(
      this.props.productStore?.pricingPage
    );
    if (!clonedPricingPage) return;
    switch (field) {
      case "itemReOrderIntervals":
        const mappedData = (options?.map((x) => {
          return x.value as keyof typeof ItemReOrderIntervals;
        }) || []) as (keyof typeof ItemReOrderIntervals)[];
        const sortedData = mappedData.sort(
          (a, b) =>
            ItemReOrderDurations[ItemReOrderIntervals[a]] -
            ItemReOrderDurations[ItemReOrderIntervals[b]]
        );

        clonedPricingPage.reOrderSettings.itemReOrderIntervals = sortedData;
        break;
      case "isLimitPurchaseQuantity":
    }

    this.props.productStore?.updatePricingPage(clonedPricingPage);
  };

  onSingleSelectChange = (field: string, value: string) => {
    const clonedPricingPage = new ProductPricingPageInputModel(
      this.props.productStore?.pricingPage
    );
    if (!clonedPricingPage) return;
    switch (field) {
      case nameof<ProductPricingPageInputModel>("pricingModel"):
        clonedPricingPage.pricingModel = value as PricingModel;
        this.props.productStore?.updatePricingPage(clonedPricingPage);
        break;
      case nameof<ProductInventorySettingsInputModel>("isInStock"):
        clonedPricingPage.inventorySettings.isInStock =
          value === "InStock" ? true : false;
        this.props.productStore?.updatePricingPage(clonedPricingPage);
        break;
    }
  };

  get basicFields() {
    return (
      <>
        <CollapsiblePanel title="Price" isShowInfoIcon={true}>
          <>
            <FlexContainer className="label-description-row">
              <LabelCell>
                <CustomLabel>Pricing Option</CustomLabel>
                <InfoIcon />
              </LabelCell>
              <DescriptionCell>
                {this.singleSelect(
                  "pricingModel",
                  this.pricingPage.pricingModel,
                  this.pricingPage.hasError("pricingModel"),
                  "pricing-option-dropdown dpdn-no-separator"
                )}
              </DescriptionCell>
            </FlexContainer>
            {this.getPricingInputs}
          </>
        </CollapsiblePanel>
      </>
    );
  }

  get advancedFields() {
    return (
      <>
        <CollapsiblePanel title="Advanced" className="collapsed-labels">
          <>
            <FlexContainer className="label-description-row payment-sub-row">
              <LabelCell>
                <CustomLabel>Inventory</CustomLabel>
              </LabelCell>
              <DescriptionCell>
                <FlexContainer className="spacing-60">
                  {this.checkBox(
                    nameof<ProductInventorySettingsInputModel>(
                      "isTrackInventory"
                    ),
                    "Track Inventory",
                    this.pricingPage.inventorySettings.isTrackInventory
                  )}
                  {this.pricingPage.inventorySettings.isTrackInventory &&
                    !this.pricingPage.inventorySettings.hideAvailable &&
                    this.textField(
                      "number",
                      nameof<ProductInventorySettingsInputModel>("available"),
                      "#Available",
                      this.pricingPage.inventorySettings.available,
                      this.pricingPage.inventorySettings.hasError("available")
                    )}

                  {!this.pricingPage.inventorySettings.isTrackInventory &&
                    !this.hasVariants &&
                    this.singleSelect(
                      "isInStock",
                      this.pricingPage.inventorySettings.isInStock
                        ? "InStock"
                        : "OutofStock",
                      this.pricingPage.hasError("pricingModel"),
                      "pricing-option-dropdown dpdn-no-separator"
                    )}
                </FlexContainer>

                {this.pricingPage.inventorySettings.isTrackInventory && (
                  <div className="invent-sub-chkbox">
                    {this.checkBox(
                      nameof<ProductInventorySettingsInputModel>(
                        "isSellOnOutOfStock"
                      ),
                      "Continue selling when out of stock",
                      this.pricingPage.inventorySettings.isSellOnOutOfStock
                    )}
                    <span className="invent-promo-chkbox">
                      {this.checkBox(
                        nameof<ProductInventorySettingsInputModel>("isPromote"),
                        "Promote when inventory is low  (< 10 units)",
                        this.pricingPage.inventorySettings.isPromote
                      )}
                    </span>
                  </div>
                )}
              </DescriptionCell>
            </FlexContainer>
            <FlexContainer className="label-description-row">
              <LabelCell></LabelCell>
              <DescriptionCell>
                <FlexContainer spacing={37}>
                  {this.checkBox(
                    nameof<ProductInventorySettingsInputModel>(
                      "isLimitPurchaseQuantity"
                    ),
                    "Limit Quantity Sold",
                    this.pricingPage.inventorySettings.isLimitPurchaseQuantity
                  )}

                  {this.pricingPage.inventorySettings
                    .isLimitPurchaseQuantity && (
                    <>
                      {this.textField(
                        "number",
                        nameof<ProductInventorySettingsInputModel>(
                          "quantityLimit"
                        ),
                        "# Units",
                        this.pricingPage.inventorySettings.quantityLimit,
                        this.pricingPage.inventorySettings.hasError(
                          "quantityLimit"
                        ),
                        "styled-input limit-qty-input"
                      )}
                      <CustomLabel
                        color="#979797"
                        fontSize="13px"
                        className="standard-lh"
                      >
                        Limit quantity that can be <br />
                        purchased at one time.
                      </CustomLabel>
                    </>
                  )}
                </FlexContainer>
              </DescriptionCell>
            </FlexContainer>
          </>
        </CollapsiblePanel>
      </>
    );
  }

  get reOrderAndSaveFiedls() {
    return (
      <>
        <CollapsiblePanel
          title="Re-Order"
          isShowInfoIcon={true}
          isShowLockIcon={true}
          className="collapsed-labels"
        >
          <>
            <FlexContainer className="label-description-row">
              <LabelCell>
                <CustomLabel lineHeight="30px !important">
                  Re-Order & Save
                </CustomLabel>
              </LabelCell>
              <DescriptionCell>
                <FlexContainer
                  className={`reorder-checkbox ${this.pricingPage.reOrderSettings.allowReOrder ? "label-description-row" : ""}`}
                >
                  {/* <Col md={12}> */}
                  {this.checkBox(
                    nameof<ProductReOrderSettingsInputModel>("allowReOrder"),
                    "Allow customers to purchase on a Subscription",
                    this.pricingPage.reOrderSettings.allowReOrder,
                    true
                  )}
                  {/* </Col> */}
                </FlexContainer>
                {this.pricingPage.reOrderSettings.allowReOrder && (
                  <>
                    <FlexContainer className="label-description-row">
                      <LabelCell>
                        <CustomLabel className="prc-multi-label">
                          Available Reorder Frequencies
                        </CustomLabel>
                      </LabelCell>
                      <DescriptionCell className="prc-multi-input">
                        {" "}
                        {this.multiSelect(
                          nameof<ProductReOrderSettingsInputModel>(
                            "itemReOrderIntervals"
                          ),
                          this.pricingPage.reOrderSettings.itemReOrderIntervals.map(
                            (x) => {
                              return {
                                value: x,
                                label: ItemReOrderIntervals[x],
                              };
                            }
                          ),
                          this.pricingPage.reOrderSettings.hasError(
                            "itemReOrderIntervals"
                          )
                            ? "error-border"
                            : "",
                          "prc-multiselect"
                        )}
                        <CustomLabel
                          color="#979797"
                          fontSize="13px"
                          className="standard-lh"
                        >
                          Choose the available Subscription Frequencies. One
                          required.
                        </CustomLabel>
                      </DescriptionCell>
                    </FlexContainer>

                    <FlexContainer>
                      <LabelCell>
                        <CustomLabel>Discount Offered</CustomLabel>
                      </LabelCell>
                      <DescriptionCell>
                        <FlexContainer
                          spacing={15}
                          fxLayout="row"
                          className="percent-input"
                        >
                          <div>
                            {this.textField(
                              "text",
                              nameof<ProductReOrderSettingsInputModel>(
                                "discountPercentage"
                              ),
                              "0.00",
                              this.pricingPage.reOrderSettings.discountPercentage.toString(),
                              this.pricingPage.reOrderSettings.hasError(
                                "discountPercentage"
                              )
                            )}
                          </div>
                        </FlexContainer>
                        <CustomLabel
                          color="#979797"
                          fontSize="13px"
                          className="standard-lh"
                        >
                          Zero for no discount.
                        </CustomLabel>
                      </DescriptionCell>
                    </FlexContainer>
                  </>
                )}
              </DescriptionCell>
            </FlexContainer>
          </>
        </CollapsiblePanel>
      </>
    );
  }

  get mediaSection() {
    const medias = this.props.productStore?.detailPage.medias || [];

    return <CarouselComponent media={medias} />;
  }

  getAdvertisingPrice() {
    const pricingModel = this.props.productStore?.pricingPage.pricingModel;

    const formatPrice = (priceValue: string) => (
      <>
        <span className="semi-bold">
          ${AccountSettingsStore.formatAmount(parseFloat(priceValue) || 0.0)}
        </span>
        <span className="usd-label"> USD</span>
      </>
    );

    switch (pricingModel) {
      case PricingModel.Standard:
        return (
          <>
            {formatPrice(
              this.props.productStore?.pricingPage.standardPricing?.price || ""
            )}
          </>
        );
      case PricingModel.Custom:
        return (
          <>
            {formatPrice(
              this.props.productStore?.pricingPage.customPricing.suggestion ||
                ""
            )}
          </>
        );
      case PricingModel.Volume:
        return (
          <>
            {formatPrice(
              this.props.productStore?.pricingPage.volumePricing.price || ""
            )}
          </>
        );
      case PricingModel.Tiered:
        return (
          <>
            {formatPrice(
              this.props.productStore?.pricingPage.tieredPricing.price || ""
            )}
          </>
        );
      case PricingModel.Package:
        const options =
          this.props.productStore?.pricingPage.packagePricing.options || [];
        // const totalPrice = options.reduce(
        //   (total, item) => total + (parseInt(item.price) || 0),
        //   0
        // );
        const totalPrice = parseFloat(options[0]?.price) || 0;
        return <>{formatPrice(totalPrice.toFixed(2))}</>;
    }
  }

  getOriginalPrice(): string {
    const pricingModel = this.props.productStore?.pricingPage.pricingModel;

    switch (pricingModel) {
      case PricingModel.Standard:
        return (
          this.props.productStore?.pricingPage.standardPricing?.originalPrice ||
          ""
        );
      case PricingModel.Custom:
        return "";
      case PricingModel.Volume:
        return (
          this.props.productStore?.pricingPage.volumePricing.originalPrice || ""
        );
      case PricingModel.Tiered:
        return (
          this.props.productStore?.pricingPage.tieredPricing.originalPrice || ""
        );
      case PricingModel.Package:
        const options =
          this.props.productStore?.pricingPage.packagePricing.options || [];
        // const totalPrice = options.reduce(
        //   (total, item) => total + (parseInt(item.originalPrice) || 0),
        //   0
        // );
        const totalPrice = parseFloat(options[0]?.originalPrice) || 0;

        return totalPrice > 0 ? totalPrice.toFixed(2) : "";
      default:
        return "";
    }
  }

  render(): React.ReactNode {
    return (
      <MainContent className="main-content product-pricing-page">
        <div className={`flex-auto-grow `}>
          <ItemContainer padding="0px 25px 0px 0px" className="float-aside">
            <ProductDetailWrapper margin="0 0 25px">
              {this.basicFields}
              {this.reOrderAndSaveFiedls}
              {this.advancedFields}
            </ProductDetailWrapper>
          </ItemContainer>
        </div>
        <MembershipRightBar className="float-block float-for-ss" width="35%">
          <AdvertisingBlockWrapper>
            {this.mediaSection}
            <CustomLabel
              fontSize="22px"
              fontWeight="600"
              color={theme.palette.common.black}
              lineHeight="24px"
              textAlign="center"
              margin="50px auto 15px"
            >
              {this.props.productStore?.detailPage.name}
            </CustomLabel>
            <CustomLabel
              // fontSize="14px"
              // textAlign="center"
              margin="0px auto 35px"
              padding="0px 10px"
              dangerouslySetInnerHTML={{
                __html: UtilityMethods.getEditorHtmlContent(
                  this.props.productStore?.detailPage
                    .descriptionEditorRawContent || ""
                ),
              }}
            />
            {this.props.productStore?.pricingPage.pricingModel ===
              PricingModel.Custom && (
              <OptionSelectorCard>
                <CustomLabel
                  fontSize="20px"
                  fontWeight="600"
                  color={theme.palette.common.black}
                  lineHeight="24px"
                  textAlign="center"
                  margin="0 0 5px"
                >
                  Enter a Price
                </CustomLabel>
                <CustomLabel
                  fontSize="12px"
                  lineHeight="16px"
                  textAlign="center"
                  margin="0 0 15px"
                >
                  Choose your own amount to purchase this item.
                </CustomLabel>
                {/* <FlexContainer
                                  alignItems="baseline"
                                  justifyContent="center"
                                  padding="0px 0px 40px"
                                  spacing={5}
                                  fxWrap="wrap"
                              >
                                  <CustomLabel
                                      fontSize="18px"
                                      color={theme.palette.common.black}
                                      fontWeight={'600'}
                                      margin="0px 0px 2px 0px"
                                  >
                                      {this.getAdvertisingPrice()}
                                  </CustomLabel>
                                  <CustomLabel fontSize={'14px'} color="#aaa">
                                      {' '}
                                      USD
                                  </CustomLabel>
                              </FlexContainer> */}
                <FlexContainer
                  spacing={15}
                  className="usd-input product-pricing-row"
                >
                  <div>
                    {this.textField(
                      "text",
                      "",
                      "",
                      this.pricingPage.reOrderSettings.discountPercentage.toString(),
                      false,
                      "styled-input"
                    )}
                  </div>
                </FlexContainer>
                <SapphireBlueButton
                  primary
                  borderRadius="20px"
                  fontSize="15px"
                  lineHeight="10px"
                  minWidth="90px"
                  style={{ minHeight: "30px" }}
                >
                  Buy
                </SapphireBlueButton>
              </OptionSelectorCard>
            )}
            {this.props.productStore?.pricingPage.pricingModel !==
              PricingModel.Custom && (
              <StyledCarousel
                nextIcon={<i className="fa fa-solid fa-caret-right"></i>}
                prevIcon={<i className="fa fa-solid fa-caret-left"></i>}
              >
                <Carousel.Item>
                  <OptionSelectorCard className="ad-selector-card">
                    <CustomLabel className="actual-price" margin="0px 0px 30px">
                      {this.getAdvertisingPrice()}
                    </CustomLabel>
                    {this.getOriginalPrice() &&
                      this.getOriginalPrice() !== "0.00" && (
                        <CustomLabel
                          className="actual-price"
                          margin="0px 0px 30px"
                          fontSize={"13px"}
                          color="#FF5900"
                          style={{ color: "#FF5900", fontSize: "13px" }}
                        >
                          Reduced from ${this.getOriginalPrice()}
                        </CustomLabel>
                      )}
                    <SapphireBlueButton
                      primary
                      borderRadius="20px"
                      fontSize="15px"
                      lineHeight="10px"
                      minWidth="90px"
                    >
                      Buy
                    </SapphireBlueButton>
                  </OptionSelectorCard>
                </Carousel.Item>
              </StyledCarousel>
            )}
          </AdvertisingBlockWrapper>
        </MembershipRightBar>
      </MainContent>
    );
  }
}
export default withRouter(ProductPricingPage);
